import { useContext, useMemo } from 'react';
import { TenderPreviewDataContextValue, TenderPreviewDataContext } from './context';
import { useQuery, ApolloError } from '@apollo/client';
import { IApiTenderPreviewResponse, IApiTenderPreviewRequest } from './types';
import { GET_TENDER_PREVIEW } from './query';
import { useLoadMp } from 'src/models/matchingProfiles/hooks';
import { groupByKeywords } from './helpers';
import { SEARCH_TYPES, HighlightColor, SearchType, MpNewKeyword } from 'src/models/matchingProfiles/types';
import { notNull } from 'src/helpers/typescript';
import { TenderPreviewData } from '.';
import { useApiTenders } from '../Tenders/hooks';
import { SearchLogic } from '@tendium/prom-types';

export function useTenderPreviewData(id: string): {
  data: TenderPreviewData | null;
  error?: ApolloError;
  loading: boolean;
  keywords: MpNewKeyword[];
} {
  const [{ vars, data: apiTendersData }] = useApiTenders();
  const { data: mpData } = useLoadMp();
  const apiTender = useMemo(
    () => apiTendersData?.tenders.find(tender => tender.id === id),
    [apiTendersData?.tenders, id]
  );

  const searchKeywords: MpNewKeyword[] = useMemo(
    () =>
      vars.keywordArgs?.map(keyword => ({
        value: keyword.value,
        highlightColor: HighlightColor.Yellow,
        searchLogic: SearchLogic.ExactMatch,
        searchType: SearchType.NoticeAndDocument
      })) ?? [],
    [vars.keywordArgs]
  );

  const isMpDocSearch = useMemo(
    () =>
      mpData?.keywords?.some(keyword => SEARCH_TYPES.includes(keyword.searchType)) ||
      searchKeywords.some(keyword => SEARCH_TYPES.includes(keyword.searchType ?? SearchType.NoticeAndDocument)),
    [mpData?.keywords, searchKeywords]
  );

  const keywords = useMemo(
    () => [
      ...(mpData?.keywords ?? [])
        .map(keyword =>
          SEARCH_TYPES.includes(keyword.searchType)
            ? {
                value: keyword.value,
                highlightColor: keyword.highlightColor,
                searchLogic: keyword.searchLogic,
                searchType: keyword.searchType
              }
            : null
        )
        .filter(notNull),
      ...searchKeywords
    ],
    [mpData?.keywords, searchKeywords]
  );

  const highlights = useMemo(() => {
    return !!keywords.length ? groupByKeywords(keywords, (!!isMpDocSearch && apiTender?.highlights) || []) : [];
  }, [keywords, isMpDocSearch, apiTender?.highlights]);

  const { data, loading, error } = useQuery<IApiTenderPreviewResponse, IApiTenderPreviewRequest>(GET_TENDER_PREVIEW, {
    variables: {
      procurementId: id,
      matchingProfileId: vars?.matchingProfileId
    },
    skip: !id,
    fetchPolicy: 'cache-first'
  });

  return useMemo(
    () => ({
      data: data?.getTender
        ? new TenderPreviewData({
            tender: data.getTender,
            highlights,
            nextId: apiTender?.nextId,
            matchedCpvs: apiTender?.matchedCpvs,
            isCommented: !!apiTender?.isCommented
          })
        : null,
      error,
      loading,
      keywords
    }),
    [
      data?.getTender,
      highlights,
      apiTender?.nextId,
      apiTender?.matchedCpvs,
      apiTender?.isCommented,
      error,
      loading,
      keywords
    ]
  );
}

export function useTenderPreview(): TenderPreviewDataContextValue {
  const contextValue = useContext(TenderPreviewDataContext);
  if (!contextValue) {
    throw new Error('This component should be placed inside TenderPreviewDataContext.');
  }

  return contextValue;
}
